import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ImageUpload({ onUploadComplete, folder = 'latinbody', currentImageUrl = '' }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImageUrl);
  const [useUrl, setUseUrl] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  useEffect(() => {
    setPreview(currentImageUrl);
  }, [currentImageUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      toast.error('Please select a valid image or video');
      return;
    }

    // Validate file size (max 50MB for videos, 10MB for images)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File must not exceed ${isVideo ? '50MB' : '10MB'}`);
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setUploading(true);
    try {
      const resourceType = isVideo ? 'video' : 'image';
      
      // Get signature from backend
      const sigResponse = await axios.get(`${API}/cloudinary/signature?resource_type=${resourceType}&folder=${folder}`);
      const sig = sigResponse.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        formData
      );

      const imageUrl = uploadResponse.data.secure_url;
      setPreview(imageUrl);
      onUploadComplete(imageUrl);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error('Error uploading file');
      setPreview(currentImageUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput) {
      toast.error('Enter a valid URL');
      return;
    }
    setPreview(urlInput);
    onUploadComplete(urlInput);
    toast.success('URL saved');
  };

  const clearImage = () => {
    setPreview('');
    setUrlInput('');
    onUploadComplete('');
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-2">
        <Button
          type="button"
          size="sm"
          variant={!useUrl ? 'default' : 'outline'}
          onClick={() => setUseUrl(false)}
          data-testid="upload-file-tab"
        >
          <Upload size={16} className="mr-1" /> Upload File
        </Button>
        <Button
          type="button"
          size="sm"
          variant={useUrl ? 'default' : 'outline'}
          onClick={() => setUseUrl(true)}
          data-testid="upload-url-tab"
        >
          Use URL
        </Button>
      </div>

      {!useUrl ? (
        <div>
          <Label htmlFor="file-upload" className="block mb-2">
            Select Image or Video
          </Label>
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <Input
                id="file-upload"
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                disabled={uploading}
                data-testid="file-input"
              />
              <p className="text-xs text-gray-500 mt-1">Max 10MB for images, 50MB for videos</p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <Label htmlFor="url-input" className="block mb-2">
            Image/Video URL
          </Label>
          <div className="flex gap-2">
            <Input
              id="url-input"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://..."
              data-testid="url-input"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={!urlInput}
              data-testid="url-submit-btn"
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="relative">
          <Label className="block mb-2">Preview</Label>
          <div className="relative inline-block">
            {preview.includes('video') || preview.endsWith('.mp4') || preview.endsWith('.mov') ? (
              <video
                src={preview}
                className="w-full max-w-md h-48 object-cover rounded-lg border"
                controls
                data-testid="video-preview"
              />
            ) : (
              <img
                src={preview}
                alt="Preview"
                className="w-full max-w-md h-48 object-cover rounded-lg border"
                data-testid="image-preview"
              />
            )}
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="absolute top-2 right-2"
              onClick={clearImage}
              data-testid="clear-image-btn"
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      )}

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="animate-spin" size={16} />
          Uploading file...
        </div>
      )}
    </div>
  );
}
